import { BaseEffect } from '../BaseEffect.js';

/**
 * Spiral effect implementation
 * Creates a mesmerizing spiral fractal with dynamic particles and distortions
 */
export class SpiralEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            speed: 1.0,           // Animation speed
            particleSize: 0.5,    // Size of particles
            spiralDensity: 0.59,  // Density of the spiral pattern
            particleCount: 4,     // Number of particles
            colorShift: 0.5,      // Amount of color shifting
            distortion: 1.0,      // Amount of spiral distortion
            particleGlow: 0.5,    // Glow intensity of particles
            imageInteraction: 0.5, // How much particles sample from image
            animate: true         // Whether to animate the effect
        };

        super('spiral', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_speed;
            uniform float u_particleSize;
            uniform float u_spiralDensity;
            uniform float u_particleCount;
            uniform float u_colorShift;
            uniform float u_distortion;
            uniform float u_particleGlow;
            uniform float u_imageInteraction;
            
            varying vec2 v_texcoord;

            float smin(float a, float b) {
                float k = 7.0;
                float res = exp(-k*a) + exp(-k*b);
                return -log(res) / k;
            }

            float sdHexPrism(vec3 p, vec2 h) {
                vec3 q = abs(p);
                return max(q.z - h.y, max((q.x*0.866025 + q.y*0.5), q.y) - h.x);
            }

            float sc(vec3 p, float r) {
                float s1 = sdHexPrism(p, vec2(r, 1e6));
                float s2 = sdHexPrism(p.yzx, vec2(r, 1e6));
                float s3 = sdHexPrism(p.zxy, vec2(r, 1e6));
                return min(s1, min(s2, s3));
            }

            float ssc(vec3 p, float r) {
                float s1 = sdHexPrism(p, vec2(r, 1e6));
                float s2 = sdHexPrism(p.yzx, vec2(r, 1e6));
                float s3 = sdHexPrism(p.zxy, vec2(r, 1e6));
                return smin(s1, smin(s2, s3));
            }

            mat2 r2d(float a) {
                float c = cos(a), s = sin(a);
                return mat2(c, s, -s, c);
            }

            // Enhanced particle function that samples from the source image
            vec4 m_particule(vec3 p, float r, float x, float d, vec2 s) {
                float t = u_time * u_speed * 0.3;
                vec3 op = p;  // Store original position
                
                // Animate position
                p.z = mod(p.z + d, 4.0) - 2.0;
                p.x += s.x*sin(t)*0.6;
                p.y += s.y*cos(t*2.0)*0.5;
                p.z += sin(t*2.0);
                
                // Calculate base particle
                float particle = length(p) - r * u_particleSize * 2.0;  // Doubled size impact
                
                // Calculate UV coordinates for sampling the image
                vec2 uv = vec2(
                    0.5 + (op.x * 0.5),
                    0.5 + (op.y * 0.5)
                );
                
                // Sample the source image
                vec4 texColor = texture2D(u_image, uv);
                float luminance = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));
                
                // Modify particle based on image luminance
                particle *= mix(1.0, 1.0 + luminance * 2.0, u_imageInteraction);
                
                // Add glow based on particle size and image color
                float glow = exp(-particle * (3.0 - u_particleGlow * 2.0));
                vec3 glowColor = mix(vec3(1.0, 0.1, 0.5), texColor.rgb, u_imageInteraction);
                
                return vec4(particle, glowColor * glow);
            }

            vec4 de(vec3 p) {
                float d = 0.0, s = 1.0;

                // Get particle distances and colors
                vec4 mp1 = m_particule(p, 0.02, 0.5, 0.0, vec2(-1.0));
                vec4 mp2 = m_particule(p, 0.045, -0.7, 3.0, vec2(-1.0, 1.0));
                vec4 mp3 = m_particule(p, 0.03, 0.1, 2.0, vec2(1.0, -1.0));
                vec4 mp4 = m_particule(p, 0.025, 0.1, 1.0, vec2(1.0, 1.0));

                // Find closest particle and its color
                vec4 particules = mp1;
                particules = particules.x < mp2.x ? particules : mp2;
                particules = particules.x < mp3.x ? particules : mp3;
                particules = particules.x < mp4.x ? particules : mp4;

                p.xy *= r2d(u_time * u_speed * 0.1 * u_distortion + p.z);

                vec3 q = p;

                for (int i = 0; i < 5; i++) {
                    q = mod(p*s + 1.0, 2.0) - 1.0;
                    d = max(d, -ssc(q, u_spiralDensity) / s);
                    s += 6.0;
                }

                if (d < particules.x)
                    return vec4(d, vec3(0.2, 0.7, 0.7));
                else
                    return particules;
            }

            void main() {
                vec2 uv = v_texcoord * 2.0 - 1.0;
                uv.x *= u_resolution.x / u_resolution.y;

                vec3 ro = vec3(0.15*cos(u_time * u_speed), 0.1*sin(u_time * u_speed), -u_time * u_speed * 0.4);
                vec3 rd = normalize(vec3(uv, -1.0));
                vec3 p = ro;

                float it = 0.0;
                vec4 res;
                for (float i = 0.0; i < 1.0; i += 0.02) {
                    it = i;
                    res = de(p);
                    if (res.x < 0.001) break;
                    p += rd*res.x*0.75;
                }

                vec3 c = res.yzw;  // Use the color from the distance estimation

                float dist = length(ro - p);
                c = mix(c, vec3(0.2, 0.1, 0.2), 1.0 - exp(-0.1 * dist*dist));
                
                // Enhanced image interaction
                vec4 texColor = texture2D(u_image, v_texcoord);
                vec3 finalColor = mix(texColor.rgb, c, u_colorShift);
                
                // Add extra glow from particles
                float glow = exp(-dist * (2.0 - u_particleGlow));
                finalColor += c * glow * u_particleGlow;

                gl_FragColor = vec4(finalColor, 1.0);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_speed', { type: '1f' });
        this.addUniform('u_particleSize', { type: '1f' });
        this.addUniform('u_spiralDensity', { type: '1f' });
        this.addUniform('u_particleCount', { type: '1f' });
        this.addUniform('u_colorShift', { type: '1f' });
        this.addUniform('u_distortion', { type: '1f' });
        this.addUniform('u_particleGlow', { type: '1f' });
        this.addUniform('u_imageInteraction', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_speed: { value: this.params.speed, type: '1f' },
            u_particleSize: { value: this.params.particleSize, type: '1f' },
            u_spiralDensity: { value: this.params.spiralDensity, type: '1f' },
            u_particleCount: { value: this.params.particleCount, type: '1f' },
            u_colorShift: { value: this.params.colorShift, type: '1f' },
            u_distortion: { value: this.params.distortion, type: '1f' },
            u_particleGlow: { value: this.params.particleGlow, type: '1f' },
            u_imageInteraction: { value: this.params.imageInteraction, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            speed: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Animation Speed'
            },
            particleSize: {
                min: 0.1,
                max: 3.0,
                step: 0.1,
                label: 'Particle Size'
            },
            particleGlow: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Particle Glow'
            },
            imageInteraction: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Image Interaction'
            },
            spiralDensity: {
                min: 0.1,
                max: 1.0,
                step: 0.01,
                label: 'Spiral Density'
            },
            particleCount: {
                min: 1,
                max: 8,
                step: 1,
                label: 'Particle Count'
            },
            colorShift: {
                min: 0,
                max: 1,
                step: 0.1,
                label: 'Color Blend'
            },
            distortion: {
                min: 0,
                max: 2,
                step: 0.1,
                label: 'Distortion'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 